import { Info } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

export interface InfoHintProps {
  /** The explanation. Used as both the visible tooltip and the trigger's accessible name. */
  label: string;
  side?: "top" | "right" | "bottom" | "left";
  className?: string;
}

/**
 * A small "ⓘ" icon that reveals a one-line explanation on hover/focus. Used to
 * gloss domain jargon (calc type, overs, markup, …) without cluttering the UI
 * with always-visible helper text.
 *
 * Self-contained: provides its own TooltipProvider so it can be dropped in
 * anywhere without app-root wiring.
 */
export function InfoHint({ label, side = "top", className }: InfoHintProps) {
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            aria-label={label}
            className={cn(
              "inline-flex shrink-0 cursor-help items-center text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-full",
              className,
            )}
          >
            <Info className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
        </TooltipTrigger>
        <TooltipContent side={side} className="max-w-xs text-balance">
          {label}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
